import { Children, isValidElement, type ReactNode } from "react";
import { urlFor } from "@/lib/sanity";
import { ContentTable } from "@/components/ContentTable";

/** Flattens a rendered heading (text, <strong>, links…) back to plain text. */
function nodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (isValidElement(node)) return nodeText(Children.toArray((node.props as any)?.children));
  return "";
}

/**
 * Stable anchor id for a body heading, so sections are deep-linkable — a
 * quoted passage can be pointed at directly instead of at the page as a whole.
 */
function headingId(children: ReactNode): string | undefined {
  const slug = nodeText(children)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return slug || undefined;
}

export const portableComponents = {
  block: {
    h2: ({ children }: any) => (
      <h2 id={headingId(children)} className="mt-12 font-display text-[24px] font-semibold leading-[32px] tracking-[-0.072px] text-text-primary">
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 id={headingId(children)} className="mt-8 font-display text-[20px] font-semibold leading-[28px] tracking-[-0.04px] text-text-primary">
        {children}
      </h3>
    ),
    normal: ({ children }: any) => (
      <p className="my-3 text-justify font-sans text-[16px] font-light leading-[22px] text-text-secondary">
        {children}
      </p>
    ),
    blockquote: ({ children }: any) => (
      <blockquote className="my-6 border-l-2 border-primary-200 pl-4 italic text-text-secondary">
        {children}
      </blockquote>
    ),
  },
  list: {
    number: ({ children }: any) => (
      <ol className="my-4 list-decimal space-y-2 pl-8 font-sans text-[16px] font-light leading-[22px] text-text-secondary marker:font-semibold marker:text-text-primary">
        {children}
      </ol>
    ),
    bullet: ({ children }: any) => (
      <ul className="my-4 list-disc space-y-2 pl-6 font-sans text-[16px] font-light leading-[22px] text-text-secondary">
        {children}
      </ul>
    ),
  },
  listItem: {
    number: ({ children }: any) => <li className="pl-1">{children}</li>,
    bullet: ({ children }: any) => <li>{children}</li>,
  },
  marks: {
    strong: ({ children }: any) => (
      <strong className="font-sans font-bold text-text-primary">{children}</strong>
    ),
    em: ({ children }: any) => <em className="italic">{children}</em>,
    link: ({ children, value }: any) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noreferrer"
        className="text-primary-600 underline underline-offset-2 hover:text-primary-700"
      >
        {children}
      </a>
    ),
  },
  types: {
    image: ({ value }: any) => {
      if (!value?.asset) return null;
      return (
        <figure className="my-10 flex flex-col items-center">
          <img
            src={urlFor(value).width(1200).url()}
            alt={value?.alt ?? ""}
            className="max-h-[480px] w-auto max-w-full rounded-md object-cover"
          />
          {value?.caption && (
            <figcaption className="mt-2 text-xs text-text-muted">{value.caption}</figcaption>
          )}
        </figure>
      );
    },
    simpleTable: ({ value }: any) => (
      <ContentTable
        headers={value?.headers ?? []}
        rows={(value?.rows ?? []).map((r: any) => r?.cells ?? [])}
      />
    ),
  },
};